<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { Dialog, DialogPanel } from '@headlessui/vue'
import { XMarkIcon, LinkIcon, ClipboardIcon, CheckIcon } from '@heroicons/vue/24/outline'
import { useCompanyStore } from '@/stores/company'
import { useAuthStore } from '@/stores/auth'
import { usePermissions } from '@/composables/usePermissions'
import { useToast } from '@/composables/useToast'
import { useI18n } from 'vue-i18n'
import { ADMIN_EXPIRATION_PERIODS, type ExpirationPeriod } from '@/domain/types/Company'
import { ROLE_LABELS, isPredefinedRoleId, type CompanyRole } from '@/domain/types/Role'
import { useCompanyRoles } from '@/composables/useCompanyRoles'
import { useSessionCustomRole, sessionCustomRoleId } from '@/composables/useSessionCustomRole'

const companyStore = useCompanyStore()
const authStore = useAuthStore()
const { userRole } = usePermissions()
const { activeRoles, fetchRoles } = useCompanyRoles()
const sessionRole = useSessionCustomRole()
const toast = useToast()
const { t } = useI18n()

const props = defineProps<{
  companyId: string
  isOpen: boolean
}>()

const emit = defineEmits(['close'])

const shareLink = ref('')
const isGenerating = ref(false)
const hasLinkCopied = ref(false)
const selectedExpiration = ref<ExpirationPeriod>('7d')
const selectedRole = ref<string>(sessionCustomRoleId() ?? 'regular')

const allExpirationOptions = [
  { value: '1h', label: t('dashboard.companyShare.expirationOptions.oneHour') },
  { value: '12h', label: t('dashboard.companyShare.expirationOptions.twelveHours') },
  { value: '1d', label: t('dashboard.companyShare.expirationOptions.oneDay') },
  { value: '7d', label: t('dashboard.companyShare.expirationOptions.sevenDays') },
  { value: '30d', label: t('dashboard.companyShare.expirationOptions.thirtyDays') },
  { value: 'never', label: t('dashboard.companyShare.expirationOptions.never') }
] as const

onMounted(() => {
  // Los roles personalizados solo los reparte un admin; el resto usa el suyo.
  if (userRole.value === 'admin' || userRole.value === 'superadmin') {
    fetchRoles().catch(() => toast.error('Error al cargar los roles'))
  }
})

/** Solo un admin puede invitar a otro admin; un regular invita a sus iguales o por debajo. */
const roleOptions = computed<Array<{ value: string; label: string }>>(() => {
  if (userRole.value === 'admin' || userRole.value === 'superadmin') {
    return [
      ...(['admin', 'regular', 'viewer'] as CompanyRole[]).map((role) => ({ value: role as string, label: ROLE_LABELS[role] })),
      ...activeRoles.value.map((r) => ({ value: r.id, label: r.name }))
    ]
  }
  const ownId = sessionCustomRoleId()
  if (ownId) {
    return [
      { value: ownId, label: sessionRole.role.value?.name ?? '' },
      { value: 'viewer', label: ROLE_LABELS.viewer }
    ]
  }
  return (['regular', 'viewer'] as CompanyRole[]).map((role) => ({ value: role as string, label: ROLE_LABELS[role] }))
})

/** Un enlace de admin caduca pronto: es la credencial más peligrosa que se puede repartir. */
const expirationOptions = computed(() =>
  selectedRole.value === 'admin'
    ? allExpirationOptions.filter((option) => ADMIN_EXPIRATION_PERIODS.includes(option.value))
    : allExpirationOptions
)

watch(selectedRole, () => {
  if (!expirationOptions.value.some((option) => option.value === selectedExpiration.value)) {
    selectedExpiration.value = expirationOptions.value[0].value
  }
  shareLink.value = ''
})

const generateShareLink = async () => {
  try {
    isGenerating.value = true
    const isPredefined = isPredefinedRoleId(selectedRole.value)
    const baseRole: CompanyRole = isPredefined ? (selectedRole.value as CompanyRole) : 'regular'
    const customRoleId = isPredefined ? undefined : selectedRole.value
    // Un regular heredado reparte los permisos que él mismo tiene, no más (como hoy).
    const permissions = isPredefined && baseRole === 'regular' && userRole.value === 'regular' && !sessionCustomRoleId()
      ? authStore.user?.permissions?.[props.companyId] : undefined

    const shareId = await companyStore.generateShareLink(
      props.companyId,
      selectedExpiration.value,
      baseRole,
      permissions,
      customRoleId
    )
    shareLink.value = `${window.location.origin}/signup?companyInvite=${shareId}`
    toast.success('Enlace generado correctamente')
  } catch (error) {
    toast.error('Error al generar el enlace')
  } finally {
    isGenerating.value = false
  }
}

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(shareLink.value)
    hasLinkCopied.value = true
    toast.success('Enlace copiado al portapapeles')
    setTimeout(() => {
      hasLinkCopied.value = false
    }, 2000)
  } catch (error) {
    toast.error('Error al copiar el enlace')
  }
}
</script>

<template>
  <Dialog :open="isOpen" @close="emit('close')" class="relative z-50">
    <div class="fixed inset-0 bg-black/30" aria-hidden="true" />
    <div class="fixed inset-0 flex items-center justify-center p-4">
      <DialogPanel class="w-full max-w-md rounded-2xl bg-white dark:bg-secondary-800 p-6 shadow-xl">
        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-lg font-semibold text-secondary-900 dark:text-white">
            {{ t('dashboard.companyShare.title') }}
          </h2>
          <button @click="emit('close')" class="text-secondary-500 hover:text-secondary-700">
            <XMarkIcon class="h-5 w-5" />
          </button>
        </div>

        <!-- Expiration Selection -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
            {{ t('dashboard.companyShare.linkValidity') }}
          </label>
          <select
            v-model="selectedExpiration"
            class="w-full rounded-lg border-secondary-300 dark:border-secondary-700 dark:bg-secondary-900"
          >
            <option v-for="option in expirationOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
        <div class="mb-6">
          <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
            {{ t('dashboard.companyShare.roleOptions.title') }}
          </label>
          <select
            v-model="selectedRole"
            class="w-full rounded-lg border-secondary-300 dark:border-secondary-700 dark:bg-secondary-900"
          >
            <option v-for="option in roleOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
          <p v-if="selectedRole === 'admin'" class="mt-2 text-xs text-amber-600 dark:text-amber-400">
            {{ t('dashboard.companyShare.adminWarning') }}
          </p>
        </div>

        <!-- Generate Link Button -->
        <button
          @click="generateShareLink"
          :disabled="isGenerating"
          class="btn-primary w-full mb-6 flex items-center justify-center gap-2"
        >
          <LinkIcon class="h-5 w-5" />
          <span>{{ t('dashboard.companyShare.generateLink') }}</span>
        </button>

        <!-- Share Link Display -->
        <div v-if="shareLink" class="space-y-2">
          <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300">
            {{ t('dashboard.companyShare.invitationLink') }}
          </label>
          <div class="flex gap-2">
            <input
              type="text"
              readonly
              :value="shareLink"
              class="flex-1 rounded-lg border-secondary-300 dark:border-secondary-700 dark:bg-secondary-900"
            />
            <button
              @click="copyToClipboard"
              class="btn-secondary flex items-center gap-2"
              :disabled="hasLinkCopied"
            >
              <ClipboardIcon v-if="!hasLinkCopied" class="h-5 w-5" />
              <CheckIcon v-else class="h-5 w-5 text-green-500" />
              <span>{{ hasLinkCopied ? t('dashboard.companyShare.copied') : t('dashboard.companyShare.copy') }}</span>
            </button>
          </div>
        </div>
      </DialogPanel>
    </div>
  </Dialog>
</template>